import type { ElementType } from 'react';
import { useMemo, useRef, useState } from 'react';
import type {
    LoaderFunctionArgs,
    MetaFunction,
    SerializeFrom,
} from '@remix-run/node';
import { json } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { useLoaderData, useLocation } from '@remix-run/react';
import { useBoolean } from 'ahooks';
import invariant from 'tiny-invariant';
import { getPreloadImageLink } from '@modules/images';
import {
    createMetaTitle,
    createMetaDescription,
    createOGTags,
    fallbackOGSocialImage,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { createProductSchema } from '@modules/seo/schema/product-schema';
import { createLDSchema } from '@modules/seo/util/create-ld-schema';
import createProductOGTags from '@modules/product/util/create-product-og-tags';
import type { loader as rootLoader } from '~/root';
import Variants from '@modules/product/component/variants/Variants';
import ProductDetails from '@modules/product/component/product-details/ProductDetails';
import { VariantSelectionProvider } from '@modules/product/context/VariantSelectionContext';
import PageHeader from '@modules/product/component/page-header/PageHeader';
import AboutSeller from '@modules/product/component/about-seller/AboutSeller';
import type {
    GetFeaturedProjectsQuery,
    GetFeaturedProjectsQueryVariables,
    GetProductMainQuery,
    GetProductMainQueryVariables,
    GetProductShippingQuery,
    GetProductShippingQueryVariables,
    GetProductVariantsProfessionalPricingQuery,
    GetProductVariantsProfessionalPricingQueryVariables,
    GetRegionsQuery,
    GetRegionsQueryVariables,
    GetShoppingCartSummaryQuery,
    GetShoppingCartSummaryQueryVariables,
} from 'generated/graphql';
import {
    GetFeaturedProjectsDocument,
    GetProductMainDocument,
    GetProductShippingDocument,
    GetProductVariantsProfessionalPricingDocument,
    GetRegionsDocument,
    GetShoppingCartSummaryDocument,
    PermissionEnum,
} from 'generated/graphql';
import { useAppDisplayModeContext, Breadcrumb } from '@archipro-design/aria';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import ProductGalleryMobile from '@modules/product/component/photo-gallery/ProductGallery.mobile';
import ProductGalleryDesktop from '~/modules/product/component/photo-gallery/ProductGallery.desktop';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import { useSetEditMode } from '@modules/root/hook/use-set-edit-mode';
import {
    assertResponse,
    response404,
} from '~/modules/root/graphql/assertResponse.server';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { extractBranchFromNetwork } from '@modules/enquiry/util/extract-branch-from-network.server';
import {
    getPermissions,
    useEditPermissions,
    usePermissions,
} from '@modules/user';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { unsafeVariant_DO_NOT_USE } from '~/utils/unsafeVariant';
import SampleEnquiryPopup from '@modules/product/component/sample-enquiry-popup/SampleEnquiryPopup';
import type { APHandle } from '@modules/root';
import { useHydrated } from 'remix-utils';
import { hasFeature } from '@modules/root';
import {
    getPrimaryCTA,
    getProductCTA,
} from '@modules/professional/util/professional-cta-helper';
import { createWarningMessages } from '@modules/root/hook/use-warning-messages';
import { processProduct } from '~/modules/product/util/process-product.server';
import { processCart } from '~/modules/product/util/process-cart.server';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import type { loader as appLoader } from '~/routes/_app';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { getSession } from '~/modules/root/util/session.server';
import StickyAction from '@modules/directory/component/StickyAction';
import EnquirePurchaseStickyAction from '@modules/product/component/variant-purchase/EnquirePurchaseStickyAction';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { getProfessionalLocations } from '~/modules/product/util/get-professional-locations';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { Link } from '@remix-run/react';
import { ProductIsland } from '~/modules/product/component/product-island/ProductIsland';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { useSetAtom } from 'jotai';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';
import { useEffect } from 'react';
import type { AssociatedPagesType } from '~/utils/country-switch';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const fallbackProductTitle =
            data?.product?.Professional?.Title &&
            data?.product?.Title &&
            `${data.product.Professional.Title} ${data.product.Title}`;

        const title = createMetaTitle(props, [
            data?.localisation?.DirectoryHeader?.Title,
            fallbackProductTitle,
        ]);

        const description = createMetaDescription([
            data?.localisation?.Description,
            data?.product?.Content,
        ]);

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            data?.product?.LibraryImages?.[0]?.LibraryImageFile
                ?.DetailPageSquare?.Desktop
        );

        const productSchema = createProductSchema(props);
        const ldSchema = createLDSchema([productSchema]);
        // ToDo: Video schema

        const preloadImages = prefetchProductImages(props);

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                title,
                description,
                meta: props,
                facebookSocialImage:
                    data?.product?.FacebookPreviewImage ?? fallbackSocialImage,
            }),
            ...createProductOGTags(
                data?.product,
                data?.shoppingCartSummary?.Currency
            ),
            ...getCanonicalHreflang(props, {
                professionalCountry: data?.product?.Professional?.Country,
            }),
            ...ldSchema,
            ...preloadImages,
            ...noIndexCrossCountry(
                data?.product?.Professional?.Country,
                rootData?.localisation?.country,
                data?.localisation
            ),
        ];
    }
);

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/modules/free-mode.min.css',
    },
];

const prefetchProductImages: MetaFunction<
    typeof loader,
    { root: typeof rootLoader; 'routes/_app': typeof appLoader }
> = (props) => {
    const { data } = props;
    if (!data?.product) {
        return [];
    }

    const defaultImage =
        data.product.DefaultImage?.LibraryImageFile?.DetailPageSquare;
    const { Desktop, Mobile } = defaultImage ?? {};

    if (!data?.product) return [];

    if (!Desktop) {
        return [];
    }

    if (Mobile && data.deviceType === 'mobile') {
        return [
            ...getPreloadImageLink(
                Mobile,
                undefined,
                getImagesSizes({ desktop: 780, mobile: 375 })
            ),
        ];
    }

    return [...getPreloadImageLink(Desktop, undefined, getImagesSizes(780))];
};

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    hideContactArchiPro: true,
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    if (formAction?.includes('add-to-shopping-cart')) {
        return false;
    }
    return defaultShouldRevalidate;
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { request, params, context } = args;
    invariant(params.urlSegment, 'A product URL segment is required');

    const { user, featureFlags } = context;
    const searchParams = new URL(request.url).searchParams;
    const productPromise = context.client.query<
        GetProductMainQuery,
        GetProductMainQueryVariables
    >({
        query: GetProductMainDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productVariantsProfessionalPricingPromise = context.client.query<
        GetProductVariantsProfessionalPricingQuery,
        GetProductVariantsProfessionalPricingQueryVariables
    >({
        query: GetProductVariantsProfessionalPricingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });

    const productShippingPromise = context.client.query<
        GetProductShippingQuery,
        GetProductShippingQueryVariables
    >({
        query: GetProductShippingDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
        },
    });
    const cartSummaryPromise = context.client.query<
        GetShoppingCartSummaryQuery,
        GetShoppingCartSummaryQueryVariables
    >({
        query: GetShoppingCartSummaryDocument,
    });

    const regionPromise = context.client.query<
        GetRegionsQuery,
        GetRegionsQueryVariables
    >({
        query: GetRegionsDocument,
        variables: {
            country: context.localisation.country,
            public: true,
        },
    });

    const getFeaturedProjectsLimit = (
        searchParams: URLSearchParams
    ): number => {
        return searchParams.get('featuredProjectsShowAll') === '1' ? 0 : 4;
    };

    const featuredProjectsPromise = context.client.query<
        GetFeaturedProjectsQuery,
        GetFeaturedProjectsQueryVariables
    >({
        query: GetFeaturedProjectsDocument,
        variables: {
            link: `/product/${params.urlSegment}`,
            limit: getFeaturedProjectsLimit(new URL(request.url).searchParams),
        },
    });

    const localisationPromise = getPageLocalisation(args);

    const [
        productResult,
        productVariantsProfessionalPricingResult,
        productShippingResult,
        cartSummaryResult,
        regionResult,
        featuredProjectsResult,
        localisation,
    ] = await Promise.all([
        productPromise,
        productVariantsProfessionalPricingPromise,
        productShippingPromise,
        cartSummaryPromise,
        regionPromise,
        featuredProjectsPromise,
        localisationPromise,
    ]);
    const ecommerceEnabled = hasFeature(featureFlags, 'ecommerce');
    const stickyPurchaseActionEnabled = hasFeature(
        featureFlags,
        'sticky_mobile_cta'
    );

    unsafeVariant_DO_NOT_USE(productResult);
    unsafeVariant_DO_NOT_USE(productVariantsProfessionalPricingResult);
    unsafeVariant_DO_NOT_USE(productShippingResult);
    unsafeVariant_DO_NOT_USE(cartSummaryResult);
    unsafeVariant_DO_NOT_USE(regionResult);
    unsafeVariant_DO_NOT_USE(featuredProjectsResult);

    const product = await assertResponse(
        args.context,
        productResult.data.Directory.Product,
        productResult.error
    );

    const productShipping = await assertResponse(
        args.context,
        productShippingResult.data.Directory.Product,
        productShippingResult.error
    );

    const productVariantsProfessionalPricing = await assertResponse(
        args.context,
        productVariantsProfessionalPricingResult.data.Directory.Product,
        productVariantsProfessionalPricingResult.error
    );

    const cartSummary = await assertResponse(
        args.context,
        cartSummaryResult.data.ShoppingCart,
        cartSummaryResult.error
    );
    invariant(cartSummary, 'Failed to load cart');

    const regions = regionResult.data.Regions;
    invariant(regions, 'Failed to load regions');

    const featuredProjects = await assertResponse(
        args.context,
        featuredProjectsResult.data.Directory.Product,
        featuredProjectsResult.error
    );

    if (!productVariantsProfessionalPricing.Professional) {
        throw response404();
    }

    const networks = productVariantsProfessionalPricing.Professional.Networks;
    const branchOptions = extractBranchFromNetwork(networks);

    const processedProduct = processProduct(
        product,
        productVariantsProfessionalPricing,
        productShipping,
        user,
        args.context.appDisplayMode
    );

    const processedCart = processCart(
        cartSummary,
        processedProduct.Professional
    );

    const processedFeaturedProjects = {
        FeaturedProjects: {
            pageInfo: featuredProjects.FeaturedProjects.pageInfo,
            edges: featuredProjects.FeaturedProjects.edges.map((edge) => {
                return {
                    node: mapProjectToTileViewModel(edge.node, {
                        appDisplayMode: context.appDisplayMode,
                        imageToDisplay: 'featured', // use the correction Thumbnail via PositionedImage
                    }),
                };
            }),
        },
    };

    const showSampleEnquiry =
        searchParams.get('sample') === '2' && product.SamplesAvailable;

    const primaryCTA = getPrimaryCTA({
        primaryCTA: processedProduct.Professional.PrimaryCTA,
        pageType: 'product',
        isManufacturer: processedProduct.Professional.IsManufacturer,
    });

    const productCTA = getProductCTA({
        productCTA: processedProduct.Professional.ProductCTA,
        pageType: 'product',
        isManufacturer: processedProduct.Professional.IsManufacturer,
    });

    // Create warning message for the WarningBanner
    const warningMessages = createWarningMessages(
        processedProduct.Professional.Country,
        context.localisation.country,
        !processedProduct.IsLive,
        'product',
        processedProduct?.Professional.Disable,
        {
            showPageNotLiveWarning: getPermissions(
                user,
                PermissionEnum.BmSupportView
            ),
            isAPStaff: getPermissions(user, PermissionEnum.BmStaffView),
        }
    );

    const { locations: showrooms, linkedProfiles } = getProfessionalLocations(
        product.Professional,
        ['Showroom']
    );

    const whereToBuyEnabled =
        hasFeature(featureFlags, 'show_where_to_buy_products') &&
        showrooms.length > 0;

    const session = await getSession(request);

    return json(
        {
            deviceType: context.device.type,
            product: processedProduct,
            shoppingCartSummary: processedCart,
            regions: regions,
            featuredProjects: processedFeaturedProjects,
            branchOptions,
            localisation,
            showSampleEnquiry,
            ecommerceEnabled,
            stickyPurchaseActionEnabled,
            primaryCTA,
            productCTA,
            warningMessages,
            whereToBuyEnabled,
            showrooms,
            linkedProfiles,
        },
        {
            headers: await session.getHeaders(),
        }
    );
};

export type ProductPageLoaderData = SerializeFrom<typeof loader>;

const ProductPage = () => {
    const {
        product,
        featuredProjects: { FeaturedProjects },
        showSampleEnquiry,
        ecommerceEnabled,
        stickyPurchaseActionEnabled,
        primaryCTA,
        productCTA,
        whereToBuyEnabled,
    } = useLoaderData<typeof loader>();

    const { mobile } = useAppDisplayModeContext();
    const hydrated = useHydrated();
    const { setCategory } = useCurrentCategory();

    const [enquiryModalOpen, { setFalse: closeEnquiryModal }] =
        useBoolean(showSampleEnquiry);
    const { canEditProducts } = useEditPermissions(product.Professional.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmMarketingView,
        product.Professional.ID
    );
    const productEditPath = getBMPagePath(
        product.Professional.URLSegment,
        'products',
        product.URLSegment
    );
    useSetEditMode(productEditPath, canEditProducts);

    //sections to hide the sticky action.
    const primaryCTASectionRef = useRef<HTMLDivElement>(null);
    const enquireSectionRef = useRef<HTMLDivElement>(null);
    const extrasSectionRef = useRef<HTMLDivElement>(null);
    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const showAllFeaturedProjects =
        searchParams.get('featuredProjectsShowAll') === '1';
    const setCountryData = useSetAtom(setCloneCountryStateAtom);

    const productRangeTiles: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            product.ProductRange.products.map((product) => {
                return {
                    model: product,
                    aspectRatio: 'square',
                    section: 'ProductRange',
                };
            }),
        [product.ProductRange.products]
    );
    const featuredProjectsTiles: TileViewModel<ProjectTileViewModel>[] =
        useMemo(
            () =>
                FeaturedProjects.edges.map(({ node }) => {
                    return {
                        model: node,
                        aspectRatio: 'default',
                        section: 'ProductFeaturedProjects',
                    };
                }),
            [FeaturedProjects.edges]
        );

    const alsoFromProductsTiles: TileViewModel<ProductTileViewModel>[] =
        useMemo(
            () =>
                product.AlsoFromProducts.products.map((product) => {
                    return {
                        model: product,
                        aspectRatio: 'square',
                        section: 'ProductRelated',
                    };
                }),
            [product.AlsoFromProducts.products]
        );
    const installersTiles: TileViewModel<ProfessionalTileViewModel>[] = useMemo(
        () =>
            product.Installers.professionals.map((professional) => {
                return {
                    model: professional,
                    aspectRatio: 'default',
                    section: 'ProductInstallers',
                };
            }),
        [product.Installers.professionals]
    );
    const productRangeAdmin = useTilesAdminData(productRangeTiles);
    const featuredProjectsAdmin = useTilesAdminData(featuredProjectsTiles);
    const alsoFromProductsAdmin = useTilesAdminData(alsoFromProductsTiles);
    const installersAdmin = useTilesAdminData(installersTiles);

    useEffect(() => {
        setCountryData({
            urlSegment: product?.URLSegment,
            currentCountry: product?.Professional?.Country,
            pageType: 'product',
            associatedPages: product?.AssociatedPages.edges.length
                ? (product?.AssociatedPages as AssociatedPagesType)
                : product?.Professional?.ProfessionalAssociatedPages,
        });
    }, [product, setCountryData]);

    // Set the current category for header navigation
    useEffect(() => {
        setCategory('Products');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <div className="pb-15 pt-8 md:px-12.5 md:pb-0 md:pt-0 qhd:mx-auto qhd:max-w-screen-qhd qhd:px-0">
            <VariantSelectionProvider product={product}>
                {!mobile && (
                    <PageHeader
                        data={{ ...product, PrimaryCTA: primaryCTA }}
                        isSticky={false}
                    />
                )}
                <div className="space-y-20 md:space-y-60">
                    <VariantOptionProvider product={product}>
                        <div className="grid grid-cols-1 gap-x-11 md:grid-cols-2 md:pt-10">
                            {mobile ? (
                                <ProductGalleryMobile
                                    data={product}
                                    title={product.Title}
                                />
                            ) : (
                                <ProductGalleryDesktop
                                    data={product}
                                    title={product.Title}
                                    loop={true}
                                />
                            )}
                            {mobile && (
                                <PageHeader
                                    data={{
                                        ...product,
                                        PrimaryCTA: primaryCTA,
                                    }}
                                />
                            )}
                            <div>
                                <div className="hidden justify-end md:-mt-[2rem] md:flex md:pb-2">
                                    <Breadcrumb
                                        logo={null}
                                        home={{
                                            link: true,
                                            as: Link,
                                            label: 'Home',
                                            to: '/',
                                            variables: {
                                                breadcrumbItemTextColor:
                                                    '#b6b6b6',
                                            },
                                        }}
                                        items={product.Breadcrumbs.map(
                                            (breadcrumb) => ({
                                                label: breadcrumb.label,
                                                href: breadcrumb.href,
                                                as: breadcrumb.as as ElementType,
                                                variables: {
                                                    breadcrumbItemTextColor:
                                                        '#b6b6b6',
                                                },
                                            })
                                        )}
                                        data-testid="breadcrumb-menu"
                                        variables={{
                                            breadcrumbFontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                                <div className="border-t border-black">
                                    <Variants
                                        data={{
                                            product: product,
                                            ecommerceEnabled,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                    <ProductDetails
                                        data={{
                                            ...product,
                                            primaryCTA,
                                            primaryCTASectionRef,
                                            enquireSectionRef,
                                        }}
                                        whereToBuyEnabled={whereToBuyEnabled}
                                        onClickWhereToBuy={() =>
                                            setWhereToBuyOpen(true)
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </VariantOptionProvider>

                    <div
                        ref={extrasSectionRef}
                        className={
                            'flex flex-col gap-y-8 px-4 md:gap-y-[8rem] md:px-0'
                        }
                    >
                        {/* Product Range */}
                        <DetailsPageRelatedTileGrid
                            type="products"
                            title={`Other products in this range`}
                            tiles={productRangeAdmin}
                            cta={
                                product.ProductRange.pageInfo.totalCount >
                                (mobile ? 4 : 8)
                                    ? {
                                          type: 'link',
                                          to: getProductRangeUrl(
                                              product.Professional,
                                              product.LibraryRange?.Title ?? ''
                                          ),
                                          label: 'View more',
                                      }
                                    : undefined
                            }
                            displayCount={{
                                desktop: 10,
                                mobile: 4,
                            }}
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                        />

                        {/* Projects featuring */}
                        <DetailsPageRelatedTileGrid
                            type="projects"
                            title={`Projects featuring ${product.Title}`}
                            tiles={featuredProjectsAdmin}
                            cta={
                                FeaturedProjects.pageInfo.totalCount <= 4
                                    ? undefined
                                    : showAllFeaturedProjects
                                      ? {
                                            type: 'link',
                                            to: '?',
                                            label: 'View less',
                                            preventScrollReset: true,
                                        }
                                      : {
                                            type: 'link',
                                            to: '?featuredProjectsShowAll=1',
                                            label: 'View more',
                                            preventScrollReset: true,
                                        }
                            }
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                            displayCount={{
                                desktop: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 8,
                                mobile: showAllFeaturedProjects
                                    ? FeaturedProjects.pageInfo.totalCount
                                    : 4,
                            }}
                        />

                        {/* Also From Products, only show if there are no products in the range */}
                        {product.ProductRange.pageInfo.totalCount === 0 && (
                            <DetailsPageRelatedTileGrid
                                type="products"
                                title={`More from ${product.Professional?.Title}`}
                                tiles={alsoFromProductsAdmin}
                                cta={
                                    product.AlsoFromProducts.pageInfo
                                        .totalCount > (mobile ? 4 : 10)
                                        ? {
                                              type: 'link',
                                              to: `${product.Professional.Link}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                                              label: 'View more',
                                          }
                                        : undefined
                                }
                                appDisplayMode={mobile ? 'mobile' : 'desktop'}
                                displayCount={{
                                    desktop: 10,
                                    mobile: 4,
                                }}
                            />
                        )}

                        {/* Installers */}
                        <DetailsPageRelatedTileGrid
                            type="professionals"
                            title={`Installers of ${product.Title}`}
                            tiles={installersAdmin}
                            cta={
                                product.Installers.pageInfo.totalCount >
                                (mobile ? 2 : 4)
                                    ? {
                                          type: 'showAll',
                                      }
                                    : undefined
                            }
                            displayCount={{
                                desktop: 4,
                                mobile: 2,
                            }}
                            appDisplayMode={mobile ? 'mobile' : 'desktop'}
                        />
                    </div>
                    {!mobile && <AboutSeller data={product} />}
                </div>
                <DetailPageButtons
                    id={product.ID}
                    editLink={productEditPath}
                    shareableLink={product.Link}
                    canEdit={canEditProducts}
                    canCopyLink={canCopyLink}
                />
                {showSampleEnquiry && hydrated && !!product.Professional && (
                    <SampleEnquiryPopup
                        data={{ product }}
                        open={enquiryModalOpen}
                        onClose={closeEnquiryModal}
                    />
                )}

                {stickyPurchaseActionEnabled && mobile && (
                    <StickyAction
                        revealBounds={[1, 3000]}
                        position={{ bottom: 0 }}
                        height={74}
                        hiddenOn={[
                            { ref: primaryCTASectionRef, threshold: 0 },
                            { ref: enquireSectionRef, threshold: 0.1 },
                            { ref: extrasSectionRef, threshold: 0 },
                        ]}
                    >
                        <EnquirePurchaseStickyAction
                            product={product}
                            primaryCTA={primaryCTA}
                        />
                    </StickyAction>
                )}
                {whereToBuyEnabled && (
                    <ProductLocationsDrawer
                        product={product}
                        open={whereToBuyOpen}
                        toggleModal={setWhereToBuyOpen}
                    />
                )}

                <ProductIsland
                    product={product}
                    professional={product.Professional}
                    productCTA={productCTA}
                />
            </VariantSelectionProvider>
        </div>
    );
};

export default ProductPage;

const getProductRangeUrl = (
    professional: { Link: string; URLSegment: string },
    rangeName: string
) => {
    const professionalProducts = `${professional.Link}/our-products`;

    // Note: Filtering is currently done using url search params which may change
    // to path params in the future.
    const searchParams = new URLSearchParams();
    if (rangeName) {
        searchParams.set('productRanges', rangeName);
    }
    if (professional.URLSegment) {
        searchParams.set('professionals', professional.URLSegment);
    }
    const constructRangeParams = searchParams.toString();

    return constructRangeParams
        ? `${professionalProducts}?${constructRangeParams}#${PROFESSIONAL_SECTIONS_ANCHOR}`
        : professionalProducts;
};
